'use client';
import { SessionContext } from '@benzinga/session-context';
import styled, { css, hueToColor } from '@benzinga/themetron';
import { noop } from '@benzinga/utils';
import React from 'react';
import { SearchTag } from '../SearchBar';
import {
  AutocompleteModule,
  ActionModule,
  SearchModule,
  TagModule,
  ClipboardModule,
  QueryScore,
  isNoCommaString,
} from './Modules';
import {
  WidgetLink,
  WidgetLinkHue,
  WidgetLinkingID,
  WidgetLinkingManager,
  isTickerSelectedEvent,
} from '@benzinga/widget-linking';
import { Tooltip } from 'antd';
import { SearchContext, SearchContextType } from '../context';
import {
  LinkingItemSpecifier,
  LinkingModuleId,
  LinkingSearchItem,
  LinkingSearchItemData,
  LinkingSearchItemVersion,
  SearchItem,
  createLinkingSearchItem,
  isLegacyLinkingSearchItem,
  isLinkingSearchItem,
} from '@benzinga/search-modules';
import Hooks from '@benzinga/hooks';
import { LinkDown, Edit } from '@benzinga/themed-icons';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';
import Styles from '../styles/styles.module.scss';

export interface LinkingSearchModule extends SearchModule<LinkingSearchItemData> {
  id: typeof LinkingModuleId;
  autocomplete: AutocompleteModule<LinkingSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

const LinkDropdown: (link?: WidgetLink) => React.FC<{ isSelected: boolean; isLocked?: boolean }> = link => props => {
  return (
    <>
      <ResultHead $isSelected={props.isSelected}>{'Link'}</ResultHead>
      <div className={Styles.resultBodyWrapper}>
        <GroupResultSwatch $tooltipColor={link?.hue ?? null} />
        <ResultDescription $isSelected={props.isSelected}>{link?.title ?? 'LINK NOT FOUND'}</ResultDescription>
        <ResultGrowFiller $isSelected={props.isSelected} />
        <ResultDescription $isSelected={props.isSelected}>
          <EditGroupIcon
            className={'u-hoverableIcon'}
            // onClickCapture={e => {
            //   e.preventDefault();
            //   e.stopPropagation();
            //   editGroup(group);
            // }}
          />
        </ResultDescription>
      </div>
    </>
  );
};

export const createLinkingAutocompleteModule = (
  widgetLinkingManager: WidgetLinkingManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): AutocompleteModule<LinkingSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isLinkingSearchItem(item)) return undefined;
      const link = widgetLinkingManager.getWidgetLinkFeedByID(item.data.linkId)?.getLink();
      return {
        component: LinkDropdown(link),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: LinkingSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isLinkingSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      const lowercaseText = text.toLowerCase();
      const allLinks = widgetLinkingManager.getWidgetLinks();
      const filteredLinkings = allLinks.filter(
        link =>
          selectedItems == null ||
          !selectedItems.some(item => isLinkingSearchItem(item) && item.data.linkId === link.id),
      );
      const scoredLinking = filteredLinkings.flatMap<[WidgetLink, QueryScore]>(link => {
        if (lowercaseText === '') {
          if (
            searchContext
              .getRecentModuleItem(widgetId)
              .some(item => isLinkingSearchItem(item) && item.data.linkId === link.id)
          ) {
            return [[link, QueryScore.HistoryMatch]];
          }
          return [[link, QueryScore.NoMatch]];
        }
        if (link.title.toLowerCase() === lowercaseText) {
          return [[link, QueryScore.FullMatch]];
        }
        if (link.title.toLowerCase().startsWith(lowercaseText)) {
          return [[link, QueryScore.StartsWithMatch]];
        }
        if (link.title.toLowerCase().includes(lowercaseText)) {
          return [[link, QueryScore.IncludesMatch]];
        }
        return [];
      });
      const sortedLinkings = scoredLinking.sort(
        (scoredLinking1, scoredLinking2) => scoredLinking2[1] - scoredLinking1[1],
      );
      const result = sortedLinkings
        .slice(undefined, limit)
        .flatMap<
          [SearchItem<LinkingSearchItemData>, QueryScore]
        >(scoredLinking => (scoredLinking[0].id ? [[createLinkingSearchItem(scoredLinking[0].id), scoredLinking[1]]] : []));
      return {
        cancel: noop,
        promise: new Promise(resolve => {
          resolve({ canceled: false, ok: result });
        }),
      };
    },
    specifier: LinkingItemSpecifier,
  };
};

const LinkingTag: React.FC<{ linkingManager: WidgetLinkingManager; linkId: WidgetLinkingID }> = ({
  linkId,
  linkingManager,
}) => {
  const update = Hooks.useForceUpdate();
  const link = linkingManager.getWidgetLinkFeedByID(linkId)?.getLink();
  Hooks.useSubscriber(linkingManager, event => {
    switch (event.type) {
      case 'linking:links_updated':
        update();
        break;
    }
  });

  return (
    <Tooltip placement={'bottom'} title={link?.title}>
      <GroupTag $tooltipColor={link?.hue ?? null}>
        <StyledLinkDown />
        {link?.events.find(isTickerSelectedEvent)?.symbol ?? link?.title ?? 'Link not found'}
      </GroupTag>
    </Tooltip>
  );
};

export const createLinkingTagModule = (widgetLinkingManager: WidgetLinkingManager): TagModule => {
  return {
    render: item => {
      if (!isLinkingSearchItem(item)) return undefined;
      return {
        jsx: <LinkingTag linkId={item.data.linkId} linkingManager={widgetLinkingManager} />,
        type: 'custom',
      };
    },
    tagInputText: item => {
      if (!isLinkingSearchItem(item)) return undefined;
      const name = widgetLinkingManager.getWidgetLinkFeedByID(item.data.linkId)?.getLink().title;
      return name ?? '';
    },
  };
};

export const createLinkingActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createLinkingClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isLinkingSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isLinkingSearchItem(token)) return undefined;
      const str = `${LinkingItemSpecifier}${token.data.linkId}`;
      return isNoCommaString(str) ? [str] : undefined;
    },
    onPasteJSON: (token): LinkingSearchItem | undefined => {
      if (isLegacyLinkingSearchItem(token)) return LinkingModuleItemLoader(token);
      return undefined;
    },
    onPasteString: str => {
      if (str.startsWith(LinkingItemSpecifier)) {
        return createLinkingSearchItem(str.slice(LinkingItemSpecifier.length));
      }
      return undefined;
    },
  };
};

const LinkingModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyLinkingSearchItem(item)) return undefined;

  switch (item.version) {
    case LinkingSearchItemVersion:
    default:
      return item;
  }
};

export const createLinkingSearchModule = (
  linkingManager: WidgetLinkingManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): LinkingSearchModule => {
  return {
    action: createLinkingActionModule(),
    autocomplete: createLinkingAutocompleteModule(linkingManager, searchContext, widgetId),
    clipboard: createLinkingClipboardModule(),
    id: LinkingModuleId,
    loader: LinkingModuleItemLoader,
    tag: createLinkingTagModule(linkingManager),
  };
};

export const useLinkingSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const linkingManager = session.getManager(WidgetLinkingManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createLinkingSearchModule(linkingManager, search, widgetContext.widgetId),
    [linkingManager, search, widgetContext.widgetId],
  );
};

const ResultHead = styled.div<{ $isSelected: boolean }>`
  width: 56px;
  margin: 3px 0;
  padding-left: 0.5em;
  font-size: 0.9em;
  color: ${props => (props.$isSelected ? props.theme.colors.foregroundActive : props.theme.colors.foregroundInactive)};
`;

const ResultDescription = styled.div<{ $isSelected: boolean }>`
  display: block;
  text-overflow: ellipsis;
  white-space: normal;
  margin: 3px 0.75em;
  color: ${props => (props.$isSelected ? props.theme.colors.foregroundActive : props.theme.colors.foreground)};
`;

const ResultGrowFiller = styled.div<{ $isSelected: boolean }>`
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 3px 0px;
  color: ${props => (props.$isSelected ? props.theme.colors.foregroundActive : props.theme.colors.foreground)};
`;

const GroupResultSwatch = styled.div<{ $tooltipColor: WidgetLinkHue }>`
  background-color: ${props =>
    props.$tooltipColor !== null
      ? css`color-mix(in srgb, ${hueToColor(props.$tooltipColor)}, ${props.theme.colors.backgroundInactive} 50%)`
      : props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  vertical-align: center;
  height: 10px;
  width: 10px;
  margin-right: 0px;
`;

const EditGroupIcon = styled(Edit)`
  fill: ${props => props.theme.colors.foregroundInactive};
  width: 1.2em;
  height: 1.2em;
  flex-shrink: 0;
`;

const GroupTag = styled(SearchTag)<{ $tooltipColor: WidgetLinkHue }>`
  background: ${props =>
    props.$tooltipColor !== null
      ? css`color-mix(in srgb, ${hueToColor(props.$tooltipColor)}, ${props.theme.colors.backgroundInactive} 50%)`
      : props.theme.colors.background};
`;

const StyledLinkDown = styled(LinkDown)`
  vertical-align: middle;
  width: 1em;
  height: 1em;
  margin-right: 4px;
`;
